import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { WarningAmber as WarningIcon } from '@mui/icons-material';
import { fmtInt } from '../../OnlineJournal/format';
import { EnrichmentState, NomenclatureMeta } from '../nomenclatureApi';
import { fmtStamp, freshnessOf } from '../nomenclatureCalc';

interface Props { meta: NomenclatureMeta }

const WARN = '#b45309';
const textSx = { fontSize: '0.78rem', color: '#64748b' };
const ERROR_SHOW_MS = 60 * 60000;

const PORTAL_WARNINGS: Record<Exclude<EnrichmentState['portal'], 'ok'>, string> = {
  no_key: 'Ключ Национального каталога не задан - сверка с НКТ остановлена',
  rejected: 'Национальный каталог отклонил ключ доступа - сверка с НКТ остановлена',
  limited: 'Национальный каталог ограничил частоту запросов - сверка продолжится чуть позже'
};

const pct = (done: number, total: number): string => (total ? `${Math.min(100, Math.floor((done / total) * 100))}%` : '-');

/** Свежесть номенклатуры 1С и ход фоновой сверки с Национальным каталогом и госреестром НЦЭЛС */
const EnrichmentStatus: React.FC<Props> = ({ meta }) => {
  const [now, setNow] = useState(() => Date.now());
  // «N мин назад» обновляется без новых запросов
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const data = meta.data;
  const enrichment = meta.enrichment;
  const stats = enrichment ? enrichment.stats : null;
  const fresh = freshnessOf(data ? data.lastUpdated : null, now);
  const loadingNow = Boolean(data && data.isLoading);
  // сервис хранит последний сбой до перезапуска - показываем только свежий
  const lastError = enrichment && enrichment.lastError && now - Date.parse(enrichment.lastError.at) < ERROR_SHOW_MS
    ? enrichment.lastError
    : null;

  if (meta.dataError || !data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, minHeight: 30 }}>
        <WarningIcon sx={{ fontSize: 18, color: WARN }} />
        <Tooltip title={meta.dataError || ''} disableHoverListener={!meta.dataError}>
          <Typography sx={{ ...textSx, color: WARN }}>Сервис номенклатуры недоступен - состояние данных неизвестно</Typography>
        </Tooltip>
      </Box>
    );
  }

  const progress = stats && (
    <Tooltip
      arrow
      title={(
        <Box sx={{ fontSize: 12, lineHeight: 1.6 }}>
          <div>Штрихкодов в 1С: {fmtInt(stats.barcodes)}, из них GTIN: {fmtInt(stats.valid)}</div>
          <div>НКТ: проверено {fmtInt(stats.nktChecked)}, карточек {fmtInt(stats.nktFound)}, только ОФД {fmtInt(stats.ofdOnly)}</div>
          <div>НЦЭЛС: лекарств {fmtInt(stats.drugs)}, проверено {fmtInt(stats.ncelsChecked)}, РУ {fmtInt(stats.ncelsFound)}, несколько РУ {fmtInt(stats.ncelsMultiple)}</div>
          <div>С изображением: {fmtInt(stats.photos)} · подлежат маркировке: {fmtInt(stats.marked)}</div>
        </Box>
      )}
    >
      <Typography sx={{ ...textSx, cursor: 'help' }}>
        Сверка: НКТ <Box component="b" sx={{ color: '#334155' }}>{pct(stats.nktChecked, stats.valid)}</Box>
        {' · '}НЦЭЛС <Box component="b" sx={{ color: '#334155' }}>{pct(stats.ncelsChecked, stats.drugs)}</Box>
        {' · '}с фото {fmtInt(stats.photos)}
      </Typography>
    </Tooltip>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', px: 0.5, minHeight: 30 }}>
      {fresh.stale && fresh.updated && !loadingNow && (
        <Tooltip title="Номенклатура из 1С обычно обновляется каждые 30 минут">
          <WarningIcon sx={{ fontSize: 18, color: WARN }} />
        </Tooltip>
      )}
      <Typography sx={textSx}>
        {fresh.updated
          ? <>Номенклатура 1С обновлена <Box component="b" sx={{ color: fresh.stale ? WARN : '#334155' }}>{fresh.updated}</Box> ({fresh.ageLabel})</>
          : 'Номенклатура 1С ещё не загружалась'}
        {fresh.updated && (
          <Tooltip title="Товаров, которые были в остатках хотя бы одной аптеки при последней загрузке. Справочник хранит и прежние позиции, поэтому в списке их больше">
            <Box component="span" sx={{ cursor: 'help' }}> · в остатках аптек: {fmtInt(data.totalItems)}</Box>
          </Tooltip>
        )}
        {data.status === 'error' && <Box component="span" sx={{ color: '#b91c1c' }}> · последнее обновление завершилось с ошибкой</Box>}
      </Typography>

      {loadingNow && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <CircularProgress size={14} />
          <Typography sx={{ ...textSx, color: '#2563eb' }}>Идёт загрузка из 1С</Typography>
        </Box>
      )}

      <Box sx={{ flexGrow: 1 }} />

      {enrichment && enrichment.pausedForSync && (
        <Tooltip title="Пока идёт обновление номенклатуры, сверка ничего не записывает. Открытые карточки сверятся сразу после него">
          <Typography sx={{ ...textSx, color: '#6d28d9' }}>
            Сверка на паузе до конца обновления 1С{enrichment.deferred ? ` (ждут: ${fmtInt(enrichment.deferred)})` : ''}
          </Typography>
        </Tooltip>
      )}
      {enrichment && enrichment.portal !== 'ok' && (
        <Typography sx={{ ...textSx, color: WARN }}>{PORTAL_WARNINGS[enrichment.portal]}</Typography>
      )}
      {lastError && (
        <Tooltip title={lastError.message || 'Сверка повторится автоматически'}>
          <Typography sx={{ ...textSx, color: WARN, cursor: 'help' }}>
            Сбой источника «{lastError.source}» {fmtStamp(lastError.at)}
          </Typography>
        </Tooltip>
      )}
      {progress}
    </Box>
  );
};

export default React.memo(EnrichmentStatus);
